import isNan from 'lodash/isNaN';
import isArray from 'lodash/isArray';
import moment from 'moment';

import * as services from 'services/templates';

export const composeValidators = (...validators) => async (
  ...validatorArgs
) => {
  for (const validator of validators) {
    const error = await validator(...validatorArgs);
    if (error) {
      return error;
    }
  }
};

export const required = value => (value ? undefined : 'Required');

export const isEqualToValueFrom = (fieldName, errorMessage) => (
  value,
  values,
  meta,
) => {
  if (meta.visited) {
    if (value !== values[fieldName]) {
      return errorMessage;
    }
  }
};

export const dateDifferenceByMinutes = differenceInMinutes => value => {
  const difference = moment(value).diff();
  return difference >= differenceInMinutes * 60 * 1000
    ? undefined
    : `Should be after ${differenceInMinutes} minutes from now`;
};

export const requiredByMinValue = min => value =>
  isNan(value) || value >= min ? undefined : 'Required';

export const requiedByMinSize = min => value =>
  !value || value.size < min ? 'Requred' : undefined;

export const arrayRequired = value =>
  isArray(value) && value.length ? undefined : 'Required';

export const mustBeNumber = value =>
  isNan(value) ? 'Must be a number' : undefined;

export const maxLength = max => value =>
  !value || value.length > max
    ? `Should have less than ${max} characters`
    : undefined;

export const minLength = min => value =>
  !value || value.length < min
    ? `Should have more than ${min} characters`
    : undefined;

export const maxValue = max => value =>
  isNan(value) || value > max ? `Should be less than ${max}` : undefined;

export const minValue = min => value =>
  isNan(value) || value >= min ? undefined : `Should be greater than ${min}`;

export const isEmail = email => {
  const regexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(email) ? undefined : 'Should be a valid email address';
};

export const isOptionalEmail = email => {
  if (typeof email === 'string' && !email) return;
  if (email?.trim === '') return undefined;
  const emailParts = email.split('@');
  const newEmail = emailParts.length > 1 ? email : `${email}@turing.com`;
  const errMsg = isEmail(newEmail);
  if (errMsg)
    return 'Should be a valid email address or email address without a domain or empty';
};

export const isDomain = domain => {
  const regexp = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
  return regexp.test(domain)
    ? undefined
    : 'Should be a valid domain name [abc.com]';
};

export const getHtmlValidationResult = async email => {
  const { result } = await services.validateHtmlTemplateV2({ template: email });
  return result;
};

export const getError = (value, validators) =>
  composeValidators(...validators)(value);

export const scanForInvalidVariables = (texts, ampTexts) => {
  const INVALID_REGEX = /{{.*}}/;
  const AMP_INVALID_REGEX = /{{(?!(result\.message)|(error\.message)).*}}/;

  let invalidVariable = '';

  texts.forEach(v => {
    if (!v) return;
    v.replace(INVALID_REGEX, variable => {
      if (variable) invalidVariable = variable;
    });
  });

  if (invalidVariable) return invalidVariable;

  if (ampTexts) {
    ampTexts.forEach(v => {
      if (!v) return;
      v.replace(AMP_INVALID_REGEX, variable => {
        if (variable) invalidVariable = variable;
      });
    });
  }

  return invalidVariable;
};

export const doesHtmlContainAmp = html => {
  const AMP_REGEX = /<amp-|(<html[^>]*)(amp|⚡)4email|(<style[^>]*)amp-.*/gi;
  return AMP_REGEX.test(html);
};

export const suggestValidTemplateVariable = (invalidVariable = '') => {
  const validVariable = invalidVariable.replace(/({{\s*)|(\s*}})/g, '%');
  if (validVariable.length <= 2) return '';
  return validVariable;
};
